import {
  CssCustomProperty,
  CssCustomPropertyReference,
} from 'theme/colors/cssCustomPropertiesUtils';
import { themes } from 'theme/colors/v2';
import _ from 'lodash';

/**
 *  Color values for agGrid theme.
 *  Colors are specified as CSS custom properties. They are injected with our CSS baseline
 *  and scoped to .ag-theme-tock.  All properties are included on our Mui theme object under the
 *  key "colors.agGrid"
 *
 *  '--ag' prefixed properties are consumed by the ag-grid theme
 *  '--ag-tock' prefixed properties are utilized for row/cell styling functions
 */

export interface AgGridColors {
  foregroundColor: string;
  backgroundColor: string;
  borderColor: string;
  dataColor: string;
  headerCellHoverBackgroundColor: string;
  headerColumnResizeHandleColor: string;
  headerForegroundColor: string;
  headerBackgroundColor: string;
  rangeSelectionBorderColor: string;
  rowHoverColor: string;
  secondaryForegroundColor: string;
  disabledForegroundColor: string;
  checkboxCheckedColor: string;
  selectedRowBackgroundColor: string;
  tockFooterBackgroundColor: string;
}

const agGridColorDefs: AgGridColors = {
  backgroundColor: themes.colors.bgDefault,
  borderColor: themes.colors.borderDefault,
  checkboxCheckedColor: themes.colors.fgPrimaryBase,
  dataColor: themes.colors.fgDefault,
  disabledForegroundColor: themes.colors.fgDisabled,
  foregroundColor: themes.colors.fgSubtle,
  headerBackgroundColor: themes.colors.bgLighter,
  headerCellHoverBackgroundColor: 'transparent',
  headerColumnResizeHandleColor: themes.colors.borderDefault,
  headerForegroundColor: themes.colors.fgDefault,
  rangeSelectionBorderColor: themes.colors.borderPrimaryBase,
  rowHoverColor: 'transparent',
  secondaryForegroundColor: themes.colors.fgSubtle,
  selectedRowBackgroundColor: themes.colors.bgBase,
  tockFooterBackgroundColor: themes.colors.bgLighter,
};

function colorToAgCss(key: string): CssCustomProperty {
  return `--ag-${_.kebabCase(key)}`;
}

/**
 * Generate CSS variables for ag-grid
 */
function generateAgTheme() {
  return Object.entries(agGridColorDefs).reduce(
    (tokens, [colorName, value]) => ({
      ...tokens,
      [colorToAgCss(colorName)]: value,
    }),
    {}
  ) as Record<CssCustomProperty, string>;
}

/**
 * Generate an object that can be used to reference the ag-grid CSS custom properties from JS
 * e.g. headerColor: var(--ag-header-color)
 */
function generateAgThemeForJs() {
  return Object.keys(agGridColorDefs).reduce(
    (generatedTheme, colorName) => {
      return {
        ...generatedTheme,
        [colorName]: `var(${colorToAgCss(colorName)})`,
      };
    },
    {} as Record<keyof AgGridColors, CssCustomPropertyReference>
  );
}

export const agGridColorTokens = generateAgTheme();
export const agGridColors = generateAgThemeForJs();
